/**
 * Quality tiers — auto-selected from a cheap device heuristic. Every world
 * system reads these knobs so the same build scales from a phone to a gaming PC.
 *
 * The AAA overhaul adds post-FX, vegetation-density, water and shadow-cascade
 * knobs. The guiding rule: the preview must be PLAYABLE on roughly 2012-era
 * hardware — Intel HD 4000 / GTX 600 class, and phone browsers — so `low` and
 * `medium` are budgeted for those and the upper tiers scale up from there.
 *
 * The single most expensive thing in the frame is flora geometry, and the knob
 * that governs it is `treeImposterRatio`, NOT the plant counts. Measured at
 * `ultra`: LOD0 ~5,060 tris/plant, LOD1 ~1,998, an imposter card 2. LOD1 is
 * only a 2.5x reduction but covers the widest distance band, so it carried
 * ~28M of the 20.76M tris actually submitted — an order of magnitude more than
 * LOD0's ~7.2M despite each plant being cheaper. Pulling `treeImposterRatio`
 * down toward `treeLod1Ratio` collapses that band into 2-triangle quads and is
 * where nearly all of the win comes from. Cards read flatter than geometry;
 * that is the deliberate trade.
 *
 * **But the two ratios must never meet.** Level assignment is outermost-first
 * and L2 subsumes L1, so `treeImposterRatio <= treeLod1Ratio` does not merely
 * shrink the LOD1 band — it deletes it, and every plant jumps from full-detail
 * geometry straight to a flat card in one step. E1b.3 found exactly that at
 * `low`/`medium`/`high` (0.30/0.32/0.35 against a 0.35 LOD1 ratio), which is
 * what the playtest saw as undergrowth popping out of existence at ~10 m.
 * The cheap way to buy the same triangles back is to pull `treeLod1Ratio` IN,
 * shrinking the expensive LOD0 band, rather than to pull `treeImposterRatio`
 * down past it.
 */

export type QualityTier = 'low' | 'medium' | 'high' | 'ultra';

export interface QualitySettings {
  tier: QualityTier;
  maxDpr: number;

  // ── Shadows ──────────────────────────────────────────────────────
  shadowsEnabled: boolean;
  shadowMapSize: number;
  /** Number of cascaded shadow-map splits (1 = plain directional shadow). */
  shadowCascades: number;
  shadowDistance: number;

  // ── Vegetation ───────────────────────────────────────────────────
  /**
   * Grass blades scattered around the player. The whole budget sits inside
   * `grassRadius` and follows the camera, so this is a density, not a total
   * over the map. 0 disables the layer entirely and is the honest A/B baseline
   * for pricing it (`?grassCount=0`), exactly as `?treeLod1Ratio=0` is for the
   * forest LOD.
   */
  grassCount: number;
  treeCount: number;
  /** Radius (m) within which grass is scattered around the player. */
  grassRadius: number;
  /**
   * Forest-floor litter fragments, on the same camera-following scatter as the
   * grass but over a smaller disc. Deferred here from B3.4, which correctly
   * judged litter to be architecturally grass rather than another fixed
   * forest layer. 0 disables it.
   */
  litterCount: number;
  /**
   * Radius (m) around the player within which grass blades bend away underfoot.
   * Rides in the wind node graph, so it does nothing when `windEnabled` is off.
   */
  grassBendRadius: number;
  /** Distance (m) past which trees swap to billboard imposters. */
  treeLodDistance: number;
  /**
   * Fraction of `treeLodDistance` at which a tree drops from LOD0 to the
   * decimated LOD1. 0 disables LOD switching entirely and renders the whole
   * forest at full detail — the pre-B3.2b behaviour, kept as an A/B baseline
   * reachable from the URL (`?treeLod1Ratio=0`).
   */
  treeLod1Ratio: number;
  /**
   * Fraction of `treeLodDistance` at which a tree drops from LOD1 to a billboard
   * imposter (B3.2c). 1 is the distance the knob is named for; below 1 brings
   * the cards nearer, which is cheaper and more obviously flat.
   *
   * MUST stay strictly greater than `treeLod1Ratio` — see the note at the top
   * of this file. At or below it there is no LOD1 band at all.
   *
   * 0 disables imposters entirely and renders LOD1 to the horizon — the
   * pre-B3.2c behaviour, kept as an A/B baseline reachable from the URL
   * (`?treeImposterRatio=0`), exactly as `treeLod1Ratio=0` is for B3.2b.
   */
  treeImposterRatio: number;
  /**
   * C1.5: whether the flora's full-detail LOD0 meshes are FETCHED at all.
   *
   * This is a download knob, not a render knob. When false the loader takes
   * only each variant's decimated LOD1 glTF and binds it to both the LOD0 and
   * LOD1 slots — 64 fewer glTF round trips and roughly half the flora geometry
   * bytes, on the tier where a 15-25 s swap-in hurts most. The forest still
   * renders three levels; its nearest one is simply the decimated mesh, which
   * at `low`'s 70 m `treeLodDistance` is already what most of the frame shows.
   *
   * True on every tier above `low`. `?floraLod0=0` is the honest A/B for
   * pricing the LOD0 fetch, in the same spirit as `?treeLod1Ratio=0`.
   *
   * A variant with no LOD1 in the manifest ignores this and loads LOD0 — a
   * plant that would otherwise vanish is worse than a plant that costs bytes.
   */
  floraLod0: boolean;
  /** Undergrowth: ground-layer ferns and cycads. */
  undergrowthCount: number;
  /**
   * Saplings (B3.4): canopy species instanced at a fraction of full size, to
   * fill the vertical gap between 2 m undergrowth and 20 m canopy. They reuse
   * the canopy glTFs already in memory, so this costs instances and draw calls
   * but no extra load time or texture residency.
   *
   * 0 disables the layer entirely — the pre-B3.4 behaviour, kept as an A/B
   * baseline reachable from the URL (`?saplingCount=0`).
   */
  saplingCount: number;
  /**
   * Deadfall (B3.4): fallen logs and stumps on the forest floor. Bark-only
   * geometry of ~100-170 triangles, so this is by far the cheapest count here.
   *
   * 0 disables the layer — the pre-B3.4 baseline, reachable as
   * `?deadfallCount=0`.
   */
  deadfallCount: number;
  /**
   * How strongly the forest clumps into dense stands and open clearings
   * (B3.6), 0..1. This reshapes WHERE the existing counts land rather than
   * adding instances, so it is deliberately NOT a cost dial and is identical
   * on every tier — a `low` player and an `ultra` player should walk the same
   * forest, thinned, exactly as terrain shape is tier-independent.
   *
   * 0 restores the pre-B3.6 uniform scatter and is the A/B baseline
   * (`?forestPatchiness=0`).
   */
  forestPatchiness: number;
  /**
   * TSL vertex wind on the flora (B3.3). Off means the wind node graph is never
   * built at all, so the tier pays nothing for it and falls back to the plain
   * non-node material — `?windEnabled=0` is the honest A/B baseline for pricing
   * the effect, in the same spirit as `treeLod1Ratio=0`.
   */
  windEnabled: boolean;
  /**
   * Overall sway amplitude multiplier. 1 is the tuned default; this scales the
   * whole effect without changing its character, so it is the knob to pull when
   * the motion reads as too seasick rather than too slow.
   */
  windStrength: number;
  /**
   * Wavelength (m) of the travelling gust wave. Neighbouring plants must be out
   * of phase or the canopy pulses as one object and reads as wallpaper — this
   * is what sets how far apart two trees have to be before they disagree.
   */
  windGustScale: number;

  // ── World ────────────────────────────────────────────────────────
  /**
   * Large rock/boulder instances scattered near guardian territories, the
   * pond, and the wider island — solid cover a crouching player can hide
   * behind. A budget, not an existence check: every tier gets cover near
   * every guardian, only the count and lobe geometry cost scale.
   */
  boulderCount: number;
  renderDistance: number;
  fogDensity: number;
  /** Terrain mesh subdivision along one edge. */
  terrainSegments: number;
  /**
   * B6.1: radial subdivisions of the island coast skirt (how many rows run
   * from the play-field boundary out to the seabed). The along-shore density
   * is NOT this knob — it must match the terrain mesh's edge subdivision so
   * the coast ring shares the terrain's edge vertices exactly and cannot
   * crack. Nothing walks on the skirt, so it stays far coarser than the play
   * mesh; this only decides how smooth the beach's falloff is.
   */
  coastSegments: number;
  /**
   * Splat-mapped PBR terrain (3 tiled layers × albedo/normal/roughness).
   * Off on `low`: it costs ~14.6 MB of PNG and nine GPU textures, and the
   * vertex-colour fallback is acceptable at that tier.
   */
  terrainSplat: boolean;

  // ── Water ────────────────────────────────────────────────────────
  // There is deliberately no `waterEnabled`. The pond and the ocean are world
  // content — the Deinosuchus guardian lives in one and the mission sends the
  // player to it — so a tier may only scale what the water COSTS, never
  // whether it exists (`.claude/rules/architecture.md`). The two knobs below
  // are that budget.
  /** Screen-space reflections on the water surface (expensive). */
  waterReflections: boolean;
  waterCaustics: boolean;

  // ── Post-processing ──────────────────────────────────────────────
  postEnabled: boolean;
  bloom: boolean;
  /** Ground-truth ambient occlusion. */
  gtao: boolean;
  godrays: boolean;
  depthOfField: boolean;
  motionBlur: boolean;
  /** 'traa' = temporal reprojection AA (best), 'fxaa' = cheap, 'none'. */
  antialias: 'traa' | 'fxaa' | 'none';
  /** Screen-space reflections on the main scene. */
  ssr: boolean;

  // ── Particles / atmosphere ───────────────────────────────────────
  /**
   * How many ambient particles (pollen motes, drifting leaf debris, insect
   * swarms) to simulate. Shares the budget with the finale — the two never run
   * at once. `AmbientParticles` clamps this to a floor of 60, so EVERY tier has
   * the effect; only its density is tiered.
   *
   * There is deliberately no `ambientParticles` boolean, and no `ambientFlyer`
   * or `islandCreatures` flag either. World content is not a quality knob — see
   * the tier rule in `.claude/rules/architecture.md`.
   */
  particleBudget: number;
  volumetricClouds: boolean;
  /**
   * Per-fragment analytic sky (smooth gradient + sun disk / horizon glow) in
   * place of the 32×16 vertex-coloured dome. Off on `low`, which keeps the
   * cheap baked-vertex dome and never constructs the shader path.
   */
  atmosphericScattering: boolean;
  /**
   * Do the player's tools (torch, thrown flares) cast real shadows?
   *
   * The torch is a point light moving through an instanced forest, which is the
   * most expensive shadow in the game — every trunk in range re-renders into a
   * cube map each frame. The light itself is cheap and stays on at every tier;
   * only the shadow is gated. `low`/`medium` get an unshadowed torch, which
   * still lights the ground and still costs the player their concealment.
   */
  toolLightShadows: boolean;
}

const PRESETS: Record<QualityTier, Omit<QualitySettings, 'tier'>> = {
  low: {
    maxDpr: 1,
    shadowsEnabled: true,
    shadowMapSize: 1024,
    shadowCascades: 1,
    shadowDistance: 45,
    grassCount: 14000,
    treeCount: 320,
    grassRadius: 17,
    litterCount: 5000,
    grassBendRadius: 2.2,
    treeLodDistance: 70,
    treeLod1Ratio: 0.20,
    treeImposterRatio: 0.42,
    floraLod0: false,
    undergrowthCount: 400,
    saplingCount: 140,
    deadfallCount: 90,
    forestPatchiness: 1,
    windEnabled: true,
    windStrength: 1,
    windGustScale: 26,
    boulderCount: 40,
    renderDistance: 300,
    fogDensity: 0.011,
    terrainSegments: 150,
    coastSegments: 4,
    terrainSplat: false,
    waterReflections: false,
    waterCaustics: false,
    postEnabled: true,
    bloom: true,
    gtao: false,
    godrays: false,
    depthOfField: false,
    motionBlur: false,
    antialias: 'fxaa',
    ssr: false,
    particleBudget: 350,
    volumetricClouds: false,
    atmosphericScattering: false,
    toolLightShadows: false,
  },
  medium: {
    maxDpr: 1.25,
    shadowsEnabled: true,
    shadowMapSize: 2048,
    shadowCascades: 2,
    shadowDistance: 90,
    grassCount: 34000,
    treeCount: 900,
    grassRadius: 24,
    litterCount: 11000,
    grassBendRadius: 2.4,
    treeLodDistance: 110,
    treeLod1Ratio: 0.20,
    treeImposterRatio: 0.45,
    floraLod0: true,
    undergrowthCount: 1200,
    saplingCount: 420,
    deadfallCount: 260,
    forestPatchiness: 1,
    windEnabled: true,
    windStrength: 1,
    windGustScale: 26,
    boulderCount: 60,
    renderDistance: 380,
    fogDensity: 0.0092,
    terrainSegments: 200,
    coastSegments: 6,
    terrainSplat: true,
    waterReflections: false,
    waterCaustics: true,
    postEnabled: true,
    bloom: true,
    gtao: true,
    godrays: true,
    depthOfField: false,
    motionBlur: false,
    antialias: 'fxaa',
    ssr: false,
    particleBudget: 1000,
    volumetricClouds: false,
    atmosphericScattering: true,
    toolLightShadows: false,
  },
  high: {
    maxDpr: 2,
    shadowsEnabled: true,
    shadowMapSize: 2048,
    shadowCascades: 3,
    shadowDistance: 180,
    grassCount: 60000,
    treeCount: 1500,
    grassRadius: 30,
    litterCount: 18000,
    grassBendRadius: 2.6,
    treeLodDistance: 170,
    treeLod1Ratio: 0.22,
    treeImposterRatio: 0.50,
    floraLod0: true,
    undergrowthCount: 1800,
    saplingCount: 620,
    deadfallCount: 400,
    forestPatchiness: 1,
    windEnabled: true,
    windStrength: 1,
    windGustScale: 26,
    boulderCount: 90,
    renderDistance: 420,
    fogDensity: 0.0078,
    terrainSegments: 260,
    coastSegments: 8,
    terrainSplat: true,
    waterReflections: true,
    waterCaustics: true,
    postEnabled: true,
    bloom: true,
    gtao: true,
    godrays: true,
    depthOfField: true,
    motionBlur: false,
    antialias: 'traa',
    ssr: false,
    particleBudget: 4000,
    volumetricClouds: true,
    atmosphericScattering: true,
    toolLightShadows: true,
  },
  ultra: {
    maxDpr: 2,
    shadowsEnabled: true,
    shadowMapSize: 4096,
    shadowCascades: 4,
    shadowDistance: 260,
    grassCount: 110000,
    treeCount: 2800,
    grassRadius: 40,
    litterCount: 32000,
    grassBendRadius: 2.8,
    treeLodDistance: 240,
    treeLod1Ratio: 0.25,
    treeImposterRatio: 0.55,
    floraLod0: true,
    undergrowthCount: 3200,
    saplingCount: 1100,
    deadfallCount: 700,
    forestPatchiness: 1,
    windEnabled: true,
    windStrength: 1,
    windGustScale: 26,
    boulderCount: 130,
    renderDistance: 560,
    fogDensity: 0.0062,
    terrainSegments: 320,
    coastSegments: 10,
    terrainSplat: true,
    waterReflections: true,
    waterCaustics: true,
    postEnabled: true,
    bloom: true,
    gtao: true,
    godrays: true,
    depthOfField: true,
    motionBlur: true,
    antialias: 'traa',
    ssr: true,
    particleBudget: 9000,
    volumetricClouds: true,
    atmosphericScattering: true,
    toolLightShadows: true,
  },
};

export const TIER_ORDER: readonly QualityTier[] = ['low', 'medium', 'high', 'ultra'];

function isMobile(): boolean {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent || '';
  const coarse = typeof matchMedia === 'function' && matchMedia('(pointer: coarse)').matches;
  return /Android|iPhone|iPad|iPod|Mobile/i.test(ua) || coarse;
}

/** Read a `?quality=high` override from the URL, if present and valid. */
function urlOverride(): QualityTier | undefined {
  if (typeof location === 'undefined') return undefined;
  const q = new URLSearchParams(location.search).get('quality');
  return TIER_ORDER.includes(q as QualityTier) ? (q as QualityTier) : undefined;
}

/**
 * Per-knob overrides from the query string, applied on top of the chosen tier:
 * `?gtao=0&antialias=fxaa`. Booleans take `0`/`1`, numbers parse as numbers,
 * everything else passes through as a string.
 *
 * This is how a single effect gets A/B'd — or bisected when one of them hangs —
 * without editing the presets and rebuilding.
 */
function applyUrlKnobs(settings: QualitySettings): QualitySettings {
  if (typeof location === 'undefined') return settings;
  const params = new URLSearchParams(location.search);
  const out = settings as unknown as Record<string, unknown>;

  for (const [key, raw] of params) {
    if (key === 'quality' || !(key in settings)) continue;
    const current = out[key];
    if (typeof current === 'boolean') out[key] = raw === '1' || raw === 'true';
    else if (typeof current === 'number') {
      const n = Number(raw);
      if (Number.isFinite(n)) out[key] = n;
    } else if (typeof current === 'string') out[key] = raw;
  }
  return settings;
}

/** Heuristic tier pick from cores, memory and form factor. */
export function detectQuality(override?: QualityTier): QualitySettings {
  const forced = override ?? urlOverride();
  if (forced) return applyUrlKnobs({ tier: forced, ...PRESETS[forced] });

  const cores = (navigator as { hardwareConcurrency?: number }).hardwareConcurrency ?? 4;
  const mem = (navigator as { deviceMemory?: number }).deviceMemory ?? 4;
  const mobile = isMobile();

  // The preview tunes for low/medium only; high/ultra are "must compile, not
  // perform" (CLAUDE.md rule 4). Core/memory counts say nothing about GPU
  // capability, so auto-detect never picks above medium — a strong machine can
  // still opt in via the in-game selector or ?quality=high.
  let tier: QualityTier;
  if (mobile) {
    tier = cores >= 8 && mem >= 6 ? 'medium' : 'low';
  } else if (cores >= 8 && mem >= 8) {
    tier = 'medium';
  } else {
    tier = 'low';
  }

  return applyUrlKnobs({ tier, ...PRESETS[tier] });
}

/** Settings for an explicit tier — used by the in-game quality selector. */
/**
 * The tier the device heuristic (and any `?quality=` URL override) would pick,
 * with no explicit caller override. Used by the landing page to show what
 * "Auto" resolves to.
 */
export function detectedTier(): QualityTier {
  return detectQuality().tier;
}

export function settingsFor(tier: QualityTier): QualitySettings {
  return { tier, ...PRESETS[tier] };
}

/**
 * Adaptive quality governor. Watches a rolling frame-time average and steps the
 * tier down when the device can't hold the target, or back up when it has
 * comfortable headroom. Essential on Android where thermal throttling is real.
 */
export class AdaptiveQuality {
  private accum = 0;
  private frames = 0;
  private cooldown = 4;
  private downgrades = 0;

  constructor(
    private tier: QualityTier,
    private readonly onChange: (tier: QualityTier) => void,
    /** Frames per second below which we step down. */
    private readonly floorFps = 40,
    /** Frames per second above which we may step back up. */
    private readonly ceilFps = 58,
  ) {}

  update(dt: number): void {
    if (this.cooldown > 0) {
      this.cooldown -= dt;
      return;
    }
    this.accum += dt;
    this.frames++;
    if (this.accum < 3) return;

    const fps = this.frames / this.accum;
    this.accum = 0;
    this.frames = 0;

    const idx = TIER_ORDER.indexOf(this.tier);
    if (fps < this.floorFps && idx > 0) {
      this.tier = TIER_ORDER[idx - 1];
      this.downgrades++;
      this.cooldown = 6;
      this.onChange(this.tier);
    } else if (fps > this.ceilFps && idx < TIER_ORDER.length - 1 && this.downgrades === 0) {
      // Only climb if we've never had to drop — avoids oscillation.
      this.tier = TIER_ORDER[idx + 1];
      this.cooldown = 10;
      this.onChange(this.tier);
    }
  }
}
